import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV !== "production" ? "http://localhost:8000" : "");
const API = `${BACKEND_URL}/api`;

function toDatetimeLocal(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [search, setSearch] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [plan, setPlan] = useState("free");
  const [disabled, setDisabled] = useState(false);
  const [planExpiresAt, setPlanExpiresAt] = useState("");
  const [customQrLimit, setCustomQrLimit] = useState("");
  const [customScanLimit, setCustomScanLimit] = useState("");
  const [customNote, setCustomNote] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (!user.is_superuser) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const loadUsers = async (q) => {
    setFetching(true);
    try {
      const url = new URL(`${API}/admin/users`);
      if (q) url.searchParams.set("search", q);
      url.searchParams.set("limit", "100");
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to load users");
        return;
      }
      setUsers(await res.json());
    } catch {
      toast.error("Failed to load users");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (!user?.is_superuser) return;
    loadUsers("");
  }, [user?.is_superuser]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [users, search]);

  const openEdit = (u) => {
    setEditing(u);
    setPlan(u.plan || "free");
    setDisabled(Boolean(u.subscription_disabled));
    setPlanExpiresAt(toDatetimeLocal(u.plan_expires_at));
    setCustomQrLimit(u.custom_plan?.qr_limit ?? "");
    setCustomScanLimit(u.custom_plan?.scan_limit ?? "");
    setCustomNote(u.custom_plan?.note ?? "");
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const body = {
        subscription_disabled: disabled,
        plan,
        plan_expires_at: planExpiresAt ? fromDatetimeLocal(planExpiresAt) : "",
        custom_plan: {
          qr_limit: customQrLimit === "" ? null : Number(customQrLimit),
          scan_limit: customScanLimit === "" ? null : Number(customScanLimit),
          note: customNote || null,
        },
      };

      const res = await fetch(`${API}/admin/users/${editing.user_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update user");
        return;
      }
      const updated = await res.json();
      setUsers((prev) => prev.map((u) => (u.user_id === updated.user_id ? updated : u)));
      toast.success("User updated");
      setEditOpen(false);
    } catch {
      toast.error("Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-[#0047FF]" />
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50" data-testid="admin-page">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <Toaster richColors position="top-right" />
        <div className="p-6 lg:p-8">
          <div className="mb-6 pl-12 lg:pl-0">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Superuser Dashboard
            </h1>
            <p className="mt-1 text-sm text-zinc-500">Manage users and subscriptions</p>
          </div>

          <Card className="border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-base font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Users
                </CardTitle>
                <div className="flex items-center gap-2">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name/email"
                    className="h-9 w-56 rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
                  />
                  <Button variant="outline" className="font-bold" onClick={() => loadUsers(search.trim())} disabled={fetching}>
                    Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-zinc-200 overflow-hidden bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50">
                      <TableHead className="text-xs font-bold text-zinc-500">User</TableHead>
                      <TableHead className="text-xs font-bold text-zinc-500">Plan</TableHead>
                      <TableHead className="text-xs font-bold text-zinc-500">Trial End</TableHead>
                      <TableHead className="text-xs font-bold text-zinc-500">Plan Expires</TableHead>
                      <TableHead className="text-xs font-bold text-zinc-500">Status</TableHead>
                      <TableHead className="text-xs font-bold text-zinc-500 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((u) => (
                      <TableRow key={u.user_id} className="border-zinc-100">
                        <TableCell>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-zinc-950 truncate">{u.name || "—"}</p>
                            <p className="text-xs text-zinc-500 truncate">{u.email}</p>
                            <p className="text-[10px] text-zinc-400 font-mono truncate">{u.user_id}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-zinc-700 font-medium">{u.plan || "free"}</TableCell>
                        <TableCell className="text-xs text-zinc-500">{u.trial_end ? new Date(u.trial_end).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="text-xs text-zinc-500">{u.plan_expires_at ? new Date(u.plan_expires_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="text-xs text-zinc-500">
                          {u.subscription_disabled ? "Disabled" : "Active"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" className="font-bold" onClick={() => openEdit(u)}>
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!filtered.length && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-sm text-zinc-500 py-10">
                          {fetching ? "Loading..." : "No users found"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Edit Subscription
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
              <p className="text-sm font-bold text-zinc-950">{editing?.name || "—"}</p>
              <p className="text-xs text-zinc-500">{editing?.email}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">Plan</label>
                <select
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                  className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
                >
                  <option value="free">free</option>
                  <option value="pro">pro</option>
                  <option value="enterprise">enterprise</option>
                  <option value="custom">custom</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">Plan Expires At</label>
                <input
                  type="datetime-local"
                  value={planExpiresAt}
                  onChange={(e) => setPlanExpiresAt(e.target.value)}
                  className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white p-3">
              <div>
                <p className="text-sm font-bold text-zinc-950">Disable Subscription</p>
                <p className="text-xs text-zinc-500">Blocks login-required features and QR scanning</p>
              </div>
              <button
                type="button"
                onClick={() => setDisabled(!disabled)}
                className={`relative h-7 w-12 rounded-full transition-colors ${disabled ? "bg-[#EF4444]" : "bg-zinc-200"}`}
              >
                <div className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${disabled ? "translate-x-[22px]" : "translate-x-0.5"}`} />
              </button>
            </div>

            <div className="rounded-lg border border-zinc-200 bg-white p-3">
              <p className="text-sm font-bold text-zinc-950 mb-1">Custom Limits</p>
              <p className="text-xs text-zinc-500 mb-3">Optional overrides for qr_limit and scan_limit</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">QR Limit</label>
                  <input
                    type="number"
                    min={0}
                    value={customQrLimit}
                    onChange={(e) => setCustomQrLimit(e.target.value)}
                    className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">Scan Limit (-1 unlimited)</label>
                  <input
                    type="number"
                    value={customScanLimit}
                    onChange={(e) => setCustomScanLimit(e.target.value)}
                    className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-2">Note</label>
                <input
                  type="text"
                  value={customNote}
                  onChange={(e) => setCustomNote(e.target.value)}
                  placeholder="Internal note"
                  className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="font-bold" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-[#0047FF] text-white hover:bg-[#0033CC] font-bold" onClick={saveEdit} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

